import { preloadExternalShare } from '@confluence/external-share/entry-points/preloadExternalShare.ts';
import { resolveAccessLinkContent } from '@confluence/external-share/entry-points/resolveAccessLinkContent.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { preloadLivePageRoute } from '@confluence/live-page-route-preloader/entry-points/preloadLivePageRoute.ts';
import { LIVE_PAGE_SUBTYPE } from '@confluence/live-pages-utils';
import { createDataFetcherForSuspense } from '@confluence/query-preloader-tools/entry-points/dataFetcherForSuspense.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { preloadPlatformNavigationQuery } from '@confluence/side-navigation/entry-points/preloadPlatformNavigationQuery.ts';
import { preloadInvitedAppShortcutsRecommendationsQuery } from '@confluence/side-navigation/entry-points/preloadInvitedAppShortcutsRecommendationsQuery.ts';
import { preloadSpacesList } from '@confluence/space-utils/entry-points/preloadSpacesList.ts';
import { fg } from '@confluence/feature-gating';

import { preloadViewPageRoute } from './preloadViewPageRoute.ts';

/**
 * The token is resolved to `contentId`/`spaceKey` first because the URL carries neither. Delegating
 * to the same preloader the content type would use off this route keeps the preloaded query
 * variables identical to the ones the rendered components use; a partial match misses the cache and
 * re-requests during render.
 *
 * The live-doc branch needs no `isAccessLinkRoute` equivalent: the only consumer of that option is
 * `preloadContent`, which `preloadLivePageRoute` never calls.
 */
const preloadAccessLinkContent = async (
	match: RouteMatch,
	url: string,
	isTransition: boolean,
	previousMatch: RouteMatch | null,
): Promise<unknown> => {
	const { shareToken } = match.params;

	if (!expValEquals('cc_perms_ga_access_links', 'isEnabled', true)) {
		return preloadExternalShare({ shareToken });
	}

	const content = await resolveAccessLinkContent(shareToken);

	// Whiteboards and anything that is not a page still render through the simplified path.
	if (!content || content.type !== 'page') {
		return preloadExternalShare({ shareToken });
	}

	const syntheticParams = {
		...match.params,
		contentId: content.contentId,
		spaceKey: content.spaceKey,
	};

	if (content.subType === LIVE_PAGE_SUBTYPE) {
		return preloadLivePageRoute({ ...match, params: syntheticParams }, url);
	}

	return preloadViewPageRoute(
		{ ...match, params: syntheticParams },
		url,
		isTransition,
		previousMatch,
		// Lets a space-scoped 403 through rather than showing the restriction screen. A flag, not
		// URL sniffing, so it holds under SSR.
		{ isAccessLinkRoute: true },
	);
};

export const preloadAccessLinkRoute = (
	match: RouteMatch,
	url: string,
	isTransition: boolean = false,
	previousMatch: RouteMatch | null = null,
): Promise<void | Error[] | unknown[]> => {
	const tasks: Promise<unknown>[] = [];

	if (expVal('shareable_links_m0', 'isEnabled', false)) {
		// Pushed as a task, not awaited: `waitForAllReturnWhenError` only absorbs rejections from
		// promises inside this array, and awaiting would serialise the side-nav preloads below
		// behind the token lookup even when the gate is off.
		tasks.push(preloadAccessLinkContent(match, url, isTransition, previousMatch));
	}

	const { accessStatus } = getPreloaderFnContext();
	const isLicensed =
		accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
		accessStatus === AccessStatus.LICENSED_USE_ACCESS;

	if (isLicensed) {
		tasks.push(
			createDataFetcherForSuspense('side-navigation', () => {
				const sideNavTasks = [preloadSpacesList({ isLicensed })];
				if (fg('navx-global-side-nav-confluence')) {
					sideNavTasks.push(preloadPlatformNavigationQuery());
					// Self-gates on the kill-switch gate + experiment; no-ops for non-cohort users.
					sideNavTasks.push(preloadInvitedAppShortcutsRecommendationsQuery());
				}
				return sideNavTasks;
			}),
		);
	}

	return waitForAllReturnWhenError(tasks);
};
